import { useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/design-system/components/ui";
import { PortalChrome } from "../components/PortalChrome";
import { LeadGenFeedbackBadgeProvider } from "../components/PortalSidebar";
import type { PortalTabId } from "../components/PortalTabMatrix";
import { usePortalAuth } from "../PortalAuthContext";
import { dashboardPathFor } from "./RecipientDashboardPage";
import { LeadGenHomeTab } from "../tabs/leadGen/HomeTab";
import { LeadGenPreleadsTab } from "../tabs/leadGen/PreleadsTab";
import { LeadGenEarningsTab } from "../tabs/leadGen/EarningsTab";
import { LeadGenFeedbackTab } from "../tabs/leadGen/FeedbackTab";

// Lead Generation System — the EXTERNAL Lead Generator console, landed at
// /portal/lead-generator. A DEDICATED page (like BrokerDashboardPage) rather
// than a RecipientDashboardPage arm: it uses PortalChrome for the branded
// shell + auth/disabled/forced-password guards.
//
// Navigation lives ENTIRELY in the shared black left rail (PortalChrome →
// PortalSidebar), the same one-nav pattern every other external portal uses:
// the four sections come from PortalTabMatrix.tabsForUser("LeadGenerator") and
// are driven by ?tab=. There is no in-page rail and no horizontal strip.
//
// Phoenix Capital is the LOAN ADMINISTRATOR — never a broker. That framing is
// carried in every string on this surface.

// The four console sections, as PortalTabIds on the shared rail.
const LEADGEN_TABS = ["home", "lg-preleads", "lg-earnings", "lg-feedback"] as const;
type LeadGenTab = (typeof LEADGEN_TABS)[number];

const TAB_META: Record<LeadGenTab, { heading: string; description: string }> = {
  home: {
    heading: "Home",
    description: "What you've racked up this week, your funnel, and where to push next.",
  },
  "lg-preleads": {
    heading: "Production line",
    description:
      "Paste a listing, extract the details, and advance each prelead one click at a time.",
  },
  "lg-earnings": {
    heading: "Earnings",
    description: "Your weekly KPI credits, caps, and where each credit stands.",
  },
  "lg-feedback": {
    heading: "Feedback",
    description: "Coaching notes from your manager.",
  },
};

function isLeadGenTab(value: string | null): value is LeadGenTab {
  return LEADGEN_TABS.includes(value as LeadGenTab);
}

export function LeadGeneratorDashboardPage() {
  const auth = usePortalAuth();
  const [params, setParams] = useSearchParams();

  const raw = params.get("tab");
  const tab: LeadGenTab = isLeadGenTab(raw) ? raw : "home";

  const setTab = useCallback(
    (next: PortalTabId) => {
      const updated = new URLSearchParams(params);
      updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  if (!auth.loading && !auth.me) {
    return <Navigate to="/portal" replace />;
  }
  if (auth.me && auth.me.accountType !== "LeadGenerator") {
    return <Navigate to={dashboardPathFor(auth.me.accountType, auth.me.kind)} replace />;
  }

  const meta = TAB_META[tab];

  return (
    <LeadGenFeedbackBadgeProvider>
      <PortalChrome pageTitle="Lead Generator console" activeTab={tab} onSelectTab={setTab}>
        <div className="space-y-space-5">
          <PageHeader
            eyebrow="Lead Generator portal"
            title={meta.heading}
            description={meta.description}
          />
          {tab === "home" && <LeadGenHomeTab onGoToPreleads={() => setTab("lg-preleads")} />}
          {tab === "lg-preleads" && <LeadGenPreleadsTab />}
          {tab === "lg-earnings" && <LeadGenEarningsTab />}
          {tab === "lg-feedback" && <LeadGenFeedbackTab />}
        </div>
      </PortalChrome>
    </LeadGenFeedbackBadgeProvider>
  );
}
